// Stage 15: Advanced Memory Module — Settings tab (final).
//
// Renders:
//   - Enable switch + explainer (all steps).
//   - Settings: intensity, idle minutes, row cap (Step 6).
//   - Migration button + last-migrated timestamp (Step 3).
//   - Recall probe (ad-hoc query box) (Step 6).
//   - Inspector: paginated list with type filter + forget per row (Step 6).
//   - Export NDJSON link (Step 6).

import { useEffect, useState, useCallback } from 'react';
import { apiGet, apiJson, apiGetRaw } from '../api/client';
import {
  listMemories,
  forgetMemory,
  migrateLegacy,
  recallMemory,
  MemoryAdvRecord,
  MemoryType,
} from '../api/memoryAdv';

interface SettingsResponse {
  settings: Record<string, string>;
}

type Intensity = 'lite' | 'standard' | 'full';

const BADGE_COLOR: Record<MemoryType, { bg: string; fg: string }> = {
  fact: { bg: '#e0f2fe', fg: '#075985' },
  event: { bg: '#fef3c7', fg: '#92400e' },
  instruction: { bg: '#d1fae5', fg: '#065f46' },
  task: { bg: '#ffe4e6', fg: '#9f1239' },
};

export function MemoryAdvTab({ onChanged }: { onChanged: () => void }) {
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [migratedAt, setMigratedAt] = useState<number>(0);
  const [ingestLastRun, setIngestLastRun] = useState<number>(0);
  const [vectorizeLastRun, setVectorizeLastRun] = useState<number>(0);
  const [intensity, setIntensity] = useState<Intensity>('lite');
  const [idleMin, setIdleMin] = useState<number>(30);
  const [maxRows, setMaxRows] = useState<number>(5000);
  const [err, setErr] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [filter, setFilter] = useState<'all' | MemoryType>('all');
  const [inclSup, setInclSup] = useState(false);
  const [inclFor, setInclFor] = useState(false);
  const [rows, setRows] = useState<MemoryAdvRecord[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loadingRows, setLoadingRows] = useState(false);

  const [recallQuery, setRecallQuery] = useState('');
  const [recallAnswer, setRecallAnswer] = useState<string | null>(null);
  const [recallBusy, setRecallBusy] = useState(false);

  const loadSettings = useCallback(async (): Promise<void> => {
    try {
      const res = await apiGet<SettingsResponse>('/api/settings');
      const s = res.settings;
      setEnabled(s.memory_advanced_enabled === 'true');
      setMigratedAt(Number(s.memory_adv_legacy_migrated_at ?? '0'));
      setIngestLastRun(Number(s.memory_adv_ingest_last_run ?? '0'));
      setVectorizeLastRun(Number(s.memory_adv_vectorize_last_run ?? '0'));
      const i = s.memory_ingest_intensity;
      if (i === 'lite' || i === 'standard' || i === 'full') setIntensity(i);
      setIdleMin(Number(s.memory_ingest_idle_minutes ?? '30'));
      setMaxRows(Number(s.memory_adv_max_rows ?? '5000'));
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  }, []);

  const loadRows = useCallback(
    async (reset: boolean): Promise<void> => {
      if (!enabled) return;
      setLoadingRows(true);
      try {
        const res = await listMemories({
          type: filter === 'all' ? undefined : filter,
          includeSuperseded: inclSup,
          includeForgotten: inclFor,
          cursor: reset ? undefined : cursor ?? undefined,
          limit: 25,
        });
        setRows((prev) => (reset ? res.memories : [...prev, ...res.memories]));
        setCursor(res.nextCursor);
      } catch (e) {
        setErr(e instanceof Error ? e.message : String(e));
      } finally {
        setLoadingRows(false);
      }
    },
    [enabled, filter, inclSup, inclFor, cursor],
  );

  useEffect(() => {
    void loadSettings();
  }, [loadSettings]);

  useEffect(() => {
    if (enabled) void loadRows(true);
    // Reset when filters change; loadRows captures the filter state.
    // Only reset on filter/flag changes, not cursor.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, filter, inclSup, inclFor]);

  const toggle = async (next: boolean): Promise<void> => {
    setBusy(true);
    setErr(null);
    setInfo(null);
    try {
      await apiJson('PUT', '/api/settings', {
        memory_advanced_enabled: next ? 'true' : 'false',
      });
      setEnabled(next);
      onChanged();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const saveSettings = async (
    key: string,
    value: string,
  ): Promise<void> => {
    try {
      await apiJson('PUT', '/api/settings', { [key]: value });
      await loadSettings();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  };

  const importLegacy = async (): Promise<void> => {
    setBusy(true);
    setErr(null);
    setInfo(null);
    try {
      const res = await migrateLegacy();
      if (res.migrated) {
        setInfo(
          `Imported legacy memory as a fact (${res.memoryId?.slice(0, 8)}…).`,
        );
      } else if (res.alreadyUpToDate) {
        setInfo('Legacy memory is already up to date — nothing to import.');
      } else {
        setInfo(res.reason ?? 'Nothing imported.');
      }
      await loadSettings();
      await loadRows(true);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const onForget = async (memoryId: string): Promise<void> => {
    if (!confirm('Mark this memory as forgotten?')) return;
    try {
      await forgetMemory(memoryId);
      await loadRows(true);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  };

  const onRecall = async (): Promise<void> => {
    const q = recallQuery.trim();
    if (!q) return;
    setRecallBusy(true);
    setRecallAnswer(null);
    try {
      const r = await recallMemory(q);
      setRecallAnswer(r.result);
    } catch (e) {
      setRecallAnswer(`Error: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setRecallBusy(false);
    }
  };

  const onExport = async (): Promise<void> => {
    // apiGetRaw fetches with the Firebase bearer token; we need a Blob URL
    // because the browser cannot follow a cross-origin <a download> to the
    // worker without adding the token as a header.
    try {
      const res = await apiGetRaw('/api/memory-adv/export?format=ndjson');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `memory-adv-${new Date().toISOString().slice(0, 10)}.ndjson`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <div className="panel">
      <h3>Memory (advanced)</h3>
      <p className="muted">
        Optional structured memory store built alongside the Markdown{' '}
        <strong>Memory</strong> tab. Stores facts, events, instructions, and
        tasks derived from prior conversations; surfaces them at prompt-
        assembly time through a five-channel retrieval pipeline.
      </p>

      <div className="row" style={{ alignItems: 'center', gap: '0.5rem' }}>
        <label
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.5rem',
          }}
        >
          <input
            type="checkbox"
            checked={enabled === true}
            disabled={enabled === null || busy}
            onChange={(e) => void toggle(e.target.checked)}
          />
          Enable advanced memory
        </label>
        {busy ? <span className="muted small">working…</span> : null}
      </div>

      {err ? <div className="status-missing">Error: {err}</div> : null}
      {info ? <div className="muted small">{info}</div> : null}

      {enabled ? (
        <>
          <div className="panel" style={{ marginTop: '1rem' }}>
            <h4 style={{ marginTop: 0 }}>Settings</h4>
            <div className="row" style={{ gap: '1rem', flexWrap: 'wrap' }}>
              <label>
                Intensity:{' '}
                <select
                  value={intensity}
                  onChange={(e) => {
                    const v = e.target.value as Intensity;
                    setIntensity(v);
                    void saveSettings('memory_ingest_intensity', v);
                  }}
                >
                  <option value="lite">lite (full pass only)</option>
                  <option value="standard">standard (+verifier)</option>
                  <option value="full">full (+detail pass)</option>
                </select>
              </label>
              <label>
                Idle minutes:{' '}
                <input
                  type="number"
                  min={1}
                  max={1440}
                  value={idleMin}
                  style={{ width: '5rem' }}
                  onChange={(e) => setIdleMin(Number(e.target.value) || 30)}
                  onBlur={() =>
                    void saveSettings(
                      'memory_ingest_idle_minutes',
                      String(idleMin),
                    )
                  }
                />
              </label>
              <label>
                Max rows:{' '}
                <input
                  type="number"
                  min={100}
                  max={100_000}
                  value={maxRows}
                  style={{ width: '6rem' }}
                  onChange={(e) => setMaxRows(Number(e.target.value) || 5000)}
                  onBlur={() =>
                    void saveSettings('memory_adv_max_rows', String(maxRows))
                  }
                />
              </label>
            </div>
            <p className="muted small" style={{ marginTop: '0.5rem' }}>
              Last auto-ingest: {formatAgo(ingestLastRun)}. Last vectorize
              drain: {formatAgo(vectorizeLastRun)}.
            </p>
          </div>

          <div className="panel" style={{ marginTop: '1rem' }}>
            <h4 style={{ marginTop: 0 }}>Legacy Markdown migration</h4>
            <div className="row">
              <button
                className="btn-primary"
                onClick={() => void importLegacy()}
                disabled={busy}
              >
                Import legacy memory
              </button>
              <span className="muted small">
                {migratedAt > 0
                  ? `Last imported: ${new Date(migratedAt * 1000).toLocaleString()}`
                  : 'Never imported.'}
              </span>
            </div>
          </div>

          <div className="panel" style={{ marginTop: '1rem' }}>
            <h4 style={{ marginTop: 0 }}>Probe recall</h4>
            <div className="row" style={{ gap: '0.5rem' }}>
              <input
                type="text"
                placeholder="ask a question against memory"
                value={recallQuery}
                onChange={(e) => setRecallQuery(e.target.value)}
                style={{ flex: 1, minWidth: '20rem' }}
              />
              <button onClick={() => void onRecall()} disabled={recallBusy}>
                Recall
              </button>
            </div>
            {recallBusy ? (
              <p className="muted small">Thinking…</p>
            ) : recallAnswer ? (
              <p
                className="small"
                style={{ whiteSpace: 'pre-wrap', marginTop: '0.5rem' }}
              >
                {recallAnswer}
              </p>
            ) : null}
          </div>

          <div className="panel" style={{ marginTop: '1rem' }}>
            <h4 style={{ marginTop: 0 }}>Inspector</h4>
            <div className="row" style={{ gap: '1rem', flexWrap: 'wrap' }}>
              <label>
                Filter:{' '}
                <select
                  value={filter}
                  onChange={(e) =>
                    setFilter(e.target.value as 'all' | MemoryType)
                  }
                >
                  <option value="all">all</option>
                  <option value="fact">fact</option>
                  <option value="event">event</option>
                  <option value="instruction">instruction</option>
                  <option value="task">task</option>
                </select>
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={inclSup}
                  onChange={(e) => setInclSup(e.target.checked)}
                />{' '}
                include superseded
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={inclFor}
                  onChange={(e) => setInclFor(e.target.checked)}
                />{' '}
                include forgotten
              </label>
              <button onClick={() => void onExport()}>Export NDJSON</button>
            </div>

            {rows.length === 0 && !loadingRows ? (
              <p className="muted small">No memories match the current filters.</p>
            ) : null}

            <ul role="list" style={{ listStyle: 'none', padding: 0, marginTop: '0.75rem' }}>
              {rows.map((r) => {
                const color = BADGE_COLOR[r.type];
                return (
                  <li
                    key={r.id}
                    role="listitem"
                    style={{
                      border: '1px solid #ddd',
                      borderRadius: 4,
                      padding: '0.5rem 0.75rem',
                      marginBottom: '0.5rem',
                    }}
                  >
                    <div
                      className="row"
                      style={{ alignItems: 'center', gap: '0.5rem' }}
                    >
                      <span
                        style={{
                          background: color.bg,
                          color: color.fg,
                          padding: '0.1rem 0.5rem',
                          borderRadius: 4,
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          textTransform: 'uppercase',
                        }}
                      >
                        {r.type}
                      </span>
                      <code
                        className="muted small"
                        title={r.id}
                        style={{ fontFamily: 'ui-monospace, SFMono-Regular' }}
                      >
                        {r.id.slice(0, 8)}…
                      </code>
                      {r.topic_key ? (
                        <code className="muted small">{r.topic_key}</code>
                      ) : null}
                      <span
                        className="muted small"
                        style={{ marginLeft: 'auto' }}
                      >
                        {new Date(r.ingested_at * 1000).toLocaleString()}
                      </span>
                    </div>
                    <p style={{ margin: '0.35rem 0 0' }}>{r.content}</p>
                    <div
                      className="row"
                      style={{
                        marginTop: '0.35rem',
                        gap: '0.75rem',
                        alignItems: 'center',
                      }}
                    >
                      {r.superseded_by ? (
                        <span className="muted small">
                          superseded by{' '}
                          <code>{r.superseded_by.slice(0, 8)}…</code>
                        </span>
                      ) : null}
                      {r.forgotten_at ? (
                        <span className="muted small">forgotten</span>
                      ) : null}
                      {!r.forgotten_at ? (
                        <button
                          className="btn-link"
                          onClick={() => void onForget(r.id)}
                          style={{ marginLeft: 'auto' }}
                        >
                          Forget
                        </button>
                      ) : null}
                    </div>
                  </li>
                );
              })}
            </ul>

            {loadingRows ? <p className="muted small">Loading…</p> : null}
            {cursor ? (
              <button onClick={() => void loadRows(false)} disabled={loadingRows}>
                Load more
              </button>
            ) : null}
          </div>
        </>
      ) : null}

      <p className="muted small" style={{ marginTop: '1rem' }}>
        The legacy single-file Markdown memory remains fully editable under
        the <strong>Memory</strong> tab regardless of this setting.
      </p>
    </div>
  );
}

function formatAgo(unixSec: number): string {
  if (!unixSec || unixSec <= 0) return 'never';
  const ageSec = Math.floor(Date.now() / 1000) - unixSec;
  if (ageSec < 60) return `${ageSec}s ago`;
  if (ageSec < 3600) return `${Math.floor(ageSec / 60)}m ago`;
  if (ageSec < 86400) return `${Math.floor(ageSec / 3600)}h ago`;
  return `${Math.floor(ageSec / 86400)}d ago`;
}
